import { Dialog, Modal, ModalOverlay } from "@admin-ui/components/primitives/application/modals/modal";
import { Button } from "@admin-ui/components/primitives/base/buttons/button";
import { CloseButton } from "@admin-ui/components/primitives/base/buttons/close-button";
import { Input } from "@admin-ui/components/primitives/base/input/input";
import { FeaturedIcon } from "@admin-ui/components/primitives/foundations/featured-icon";
import { Image01 } from "@untitledui/icons";
import { useState } from "react";
import { Heading as AriaHeading } from "react-aria-components";
import { useDebouncedSearch } from "@/hooks/use-debounced-search";

interface StockPhotoResult {
  alt: string;
  height: number;
  id: number;
  url_original: string;
  url_preview: string;
  width: number;
}

interface StockPhotoSearchModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSelect: (urlOriginal: string, urlPreview: string) => void;
  searchPath: string;
}

export function StockPhotoSearchModal({ isOpen, onOpenChange, onSelect, searchPath }: StockPhotoSearchModalProps) {
  const {
    query,
    setQuery,
    results,
    state: searchState,
    reset,
  } = useDebouncedSearch<StockPhotoResult>(searchPath, { delayMs: 200, enabled: isOpen });
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const resetState = () => {
    reset();
    setSelectedId(null);
  };

  const handleAnimationEnd = () => {
    if (!isOpen) resetState();
  };

  const handleConfirm = () => {
    const selected = results.find((r) => r.id === selectedId);
    if (selected) {
      onSelect(selected.url_preview, selected.url_preview);
      onOpenChange(false);
    }
  };

  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={onOpenChange} onAnimationEnd={handleAnimationEnd} isDismissable>
      <Modal>
        <Dialog>
          {({ close }) => (
            <div className="pointer-events-auto relative w-full overflow-hidden rounded-2xl bg-primary shadow-xl sm:max-w-[640px]">
              <CloseButton
                onPress={close}
                theme="light"
                size="sm"
                className="absolute top-3 right-3 z-10 sm:top-4 sm:right-4"
              />

              <div className="flex flex-col gap-5 px-4 pt-5 sm:px-6 sm:pt-6">
                <div className="flex flex-col gap-4">
                  <FeaturedIcon color="brand" size="md" theme="outline" icon={Image01} />
                  <div className="flex flex-col gap-1">
                    <AriaHeading slot="title" className="text-lg font-semibold text-primary">
                      Search stock photos
                    </AriaHeading>
                    <p className="text-sm text-tertiary">Search for a destination to find stock photos.</p>
                  </div>
                </div>

                <Input
                  label="Search"
                  placeholder="e.g. Bangkok, Berlin, Bali..."
                  value={query}
                  onChange={setQuery}
                  className="label:hidden"
                />

                <div className="min-h-[240px]">
                  {searchState === "idle" && (
                    <p className="py-8 text-center text-sm text-tertiary">
                      Type a destination name to search for photos.
                    </p>
                  )}

                  {searchState === "loading" && (
                    <div className="grid grid-cols-3 gap-2">
                      {Array.from({ length: 6 }, (_, i) => (
                        <div
                          key={`skeleton-${String(i)}`}
                          className="aspect-video animate-pulse rounded-lg bg-secondary"
                        />
                      ))}
                    </div>
                  )}

                  {searchState === "empty" && (
                    <p className="py-8 text-center text-sm text-tertiary">
                      No photos found for &ldquo;{query}&rdquo;. Try a different search term.
                    </p>
                  )}

                  {searchState === "results" && (
                    <div className="grid grid-cols-3 gap-2">
                      {results.map((photo) => (
                        <button
                          key={photo.id}
                          type="button"
                          onClick={() => setSelectedId(photo.id === selectedId ? null : photo.id)}
                          className={`overflow-hidden rounded-lg ring-2 transition-all ${
                            photo.id === selectedId ? "ring-brand shadow-md" : "ring-transparent hover:ring-secondary"
                          }`}
                        >
                          <img
                            src={photo.url_preview}
                            alt={photo.alt}
                            className="aspect-video w-full object-cover"
                            loading="lazy"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3 border-t border-secondary p-4 sm:px-6">
                <Button size="md" color="secondary" onClick={close}>
                  Cancel
                </Button>
                <Button size="md" color="primary" isDisabled={selectedId == null} onClick={handleConfirm}>
                  Use this photo
                </Button>
              </div>
            </div>
          )}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
